import { useEffect, useMemo } from "preact/hooks"
import { useState } from "preact/compat"
import { useFetch, exactCurrencyFormatter, usePageTitle, map, toISODateString, useAsyncEffect } from "@/utils"
import { CheckNavbar, GlobalFilters } from "@/app/navbar"
import { useLocation } from "wouter-preact"
import { Button } from "@/components/ui/button"
import { GlobalFilterState } from "@/global_filter"
import type { DateKind } from "@/global_filter"
import { posting_date_filter_enabled } from "@/global_filter/posting_date_filter.ts"
import { OrgConfigState, loadOrgFieldConfigs } from "@/org_config/state.tsx"
import { api_fetch_throws, CheckDetail } from "@/api"
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  useReactTable,
  SortingState,
  ColumnFiltersState,
  VisibilityState,
  Table,
} from "@tanstack/react-table"
import { TableControls } from "@/deduction_table/table_controls.tsx"
import { PageControls } from "@/deduction_table/page_controls.tsx"
import { ColumnChoice, ColumnChoiceFilter, ColumnHeader } from "@/deduction_table/column_header.tsx"
import { AccountingStatus } from "./accounting_transaction.tsx"
import { format, parseISO } from "date-fns"
import { capitalCase } from "change-case"
import { Skeleton } from "@/components/ui/skeleton.tsx"
import { fuzzyFilter } from "@/utils/filters.ts"
import { Copy, Check, File } from "lucide-react"
import { CopyableCell } from "@/components/copyable_cell.tsx"
import { handleViewS3Uri } from "@/deduction/[id]/UserFiles.tsx"
import { applied_remote_link } from "@/accounting/cash_application_display.ts"
import { useOrgSettings } from "@/org_settings/state.tsx"
import { check_detail_path } from "./check_path"
import { check_applied_state, ManualAppliedToggle } from "./manual_applied_toggle"
import { BulkApplyDialog } from "./BulkApplyDialog"
import { get_integration_label } from "./CashApplicationControl"
import { distinct_customers } from "./open_deductions_display"

const RIGHT_ALIGNED_COLUMN_IDS = new Set([
  "amount",
  "computed_check_total",
  "invoices_total",
  "repayments_total",
  "deductions_total",
  "deduction_rate_pct",
])

export function format_deduction_rate_pct(rate: number | null | undefined): string {
  if (rate == null || !Number.isFinite(rate)) {
    return "—"
  }
  return `${rate.toFixed(1)}%`
}

/** Green below 20%, red above 30%, neutral between (inclusive boundaries). */
export function check_deduction_rate_color_class(rate_pct: number | null | undefined): string {
  if (rate_pct == null || !Number.isFinite(rate_pct)) {
    return "text-muted-foreground"
  }
  if (rate_pct < 20) {
    return "text-green-600"
  }
  if (rate_pct > 30) {
    return "text-red-600"
  }
  return ""
}

function currency_cell(value: number | undefined | null) {
  return <div class="text-right font-medium">{value != null ? exactCurrencyFormatter(value) : "-"}</div>
}

/** Patch one list row with the `check` row returned after a manual applied toggle. */
export function apply_manual_applied_update(rows: CheckDetail[], number: string, updated: CheckDetail): CheckDetail[] {
  return rows.map((row: CheckDetail) =>
    row.number === number
      ? { ...row, id: updated.id ?? row.id, applied_at: updated.applied_at ?? undefined, applied_source: updated.applied_source ?? undefined }
      : row
  )
}

/**
 * The checks a bulk apply should try: every unapplied check in the list as filtered, oldest first,
 * so an invoice paid across two checks takes its payments in the order they arrived.
 */
export function bulk_apply_candidates(rows: CheckDetail[]): CheckDetail[] {
  return rows
    .filter((row: CheckDetail) => check_applied_state(row) === "not_applied")
    .sort((a: CheckDetail, b: CheckDetail) => (a.date ?? "").localeCompare(b.date ?? "") || a.number.localeCompare(b.number))
}

/** Mark one list row applied once the bulk run has created its payment. */
export function apply_bulk_applied_update(rows: CheckDetail[], number: string, payment_remote_id: string | undefined): CheckDetail[] {
  return rows.map((row: CheckDetail) =>
    row.number === number
      ? { ...row, applied_payment_remote_id: payment_remote_id ?? row.applied_payment_remote_id, applied_at: row.applied_at ?? new Date().toISOString() }
      : row
  )
}

/** The Customer column's filter: the exact customer name picked in the customer select. */
export function customer_equals_filter(row: { getValue: (id: string) => unknown }, column_id: string, filter_value: unknown): boolean {
  return row.getValue(column_id) === filter_value
}

/** The customer select's options: every customer among the loaded checks, A to Z. */
export function check_customer_choices(rows: Pick<CheckDetail, "customer_name">[]): ColumnChoice[] {
  return distinct_customers(rows).map((name: string) => ({ value: name, label: name }))
}

/** The customer picked in the column filters, if any. */
export function selected_check_customer(filters: ColumnFiltersState): string | undefined {
  const value = filters.find((filter) => filter.id === "customer_name")?.value
  return typeof value === "string" && value !== "" ? value : undefined
}

/**
 * The customer filter to open the page with: the one last picked in this tab, kept only while a
 * loaded check still belongs to it (a new date range can leave it with nothing to show). `choices`
 * is null while the checks are still loading.
 */
export function restored_customer_filter(stored: string | null | undefined, choices: ColumnChoice[] | null): ColumnFiltersState {
  if (!stored) return []
  if (choices && !choices.some((choice: ColumnChoice) => choice.value === stored)) return []
  return [{ id: "customer_name", value: stored }]
}

// Per-tab convenience so Back from a check returns to the same customer's list.
const CUSTOMER_FILTER_STORAGE_KEY = "accounting_checks.customer"

function read_stored_customer(): string | null {
  try {
    return window.sessionStorage.getItem(CUSTOMER_FILTER_STORAGE_KEY)
  } catch {
    return null
  }
}

function write_stored_customer(customer: string | undefined) {
  try {
    if (customer) {
      window.sessionStorage.setItem(CUSTOMER_FILTER_STORAGE_KEY, customer)
    } else {
      window.sessionStorage.removeItem(CUSTOMER_FILTER_STORAGE_KEY)
    }
  } catch {
    // Storage blocked: the filter still works, it is just not remembered.
  }
}

function build_check_list_columns(
  show_posting_date: boolean,
  posting_date_label: string,
  accounting_integration: string | null,
  customer_choices: ColumnChoice[],
  on_applied_change: (number: string, updated: CheckDetail) => void
): ColumnDef<CheckDetail>[] {
  const columns: ColumnDef<CheckDetail>[] = [
    {
      accessorKey: "number",
      header: ({ column }) => <ColumnHeader column={column} title="Check Number" />,
      enableGlobalFilter: true,
      cell: ({ getValue }) => {
        const value = getValue() as string
        return <CopyableCell value={value} />
      },
    },
    {
      accessorKey: "date",
      header: ({ column }) => <ColumnHeader column={column} title="Date" />,
      enableGlobalFilter: true,
      cell: ({ row }) => {
        const date = row.getValue("date") as string
        return <div>{date ? format(parseISO(date), "PP") : "-"}</div>
      },
    },
  ]

  if (show_posting_date) {
    columns.push({
      accessorKey: "posting_date",
      header: ({ column }) => <ColumnHeader column={column} title={posting_date_label} />,
      enableGlobalFilter: true,
      cell: ({ row }) => {
        const date = row.getValue("posting_date") as string | null | undefined
        return <div>{date ? format(parseISO(date), "PP") : "-"}</div>
      },
    })
  }

  columns.push(
    {
      accessorKey: "customer_name",
      header: ({ column }) => (
        <ColumnHeader
          column={column}
          title="Customer"
          filterContent={<ColumnChoiceFilter column={column} choices={customer_choices} all_label="All customers" className="min-w-56" />}
        />
      ),
      filterFn: customer_equals_filter,
      enableGlobalFilter: true,
      cell: ({ row }) => {
        const customerName = row.getValue("customer_name") as string | undefined
        return <div>{customerName || "-"}</div>
      },
    },
    {
      accessorKey: "status",
      header: ({ column }) => <ColumnHeader column={column} title="Status" />,
      enableGlobalFilter: true,
      cell: ({ row }) => {
        const status = row.getValue("status") as string
        // AccountingStatus only supports "new" | "split" | "coded", so handle "exported" separately
        if (status === "exported") {
          return <span class="px-2 py-1 font-medium rounded-sm bg-green-500">Exported</span>
        }
        return <AccountingStatus status={status as "new" | "split" | "coded"} />
      },
    },
    {
      id: "applied",
      accessorFn: (row: CheckDetail) => (check_applied_state(row) === "not_applied" ? "not applied" : "applied"),
      header: ({ column }) => <ColumnHeader column={column} title="Applied" />,
      enableGlobalFilter: true,
      cell: ({ row }) => {
        const paymentId = row.original.applied_payment_remote_id
        const creditMemoId = row.original.applied_credit_memo_remote_id
        const applied_state = check_applied_state(row.original)
        if (applied_state !== "remote") {
          const manual_toggle = (
            <ManualAppliedToggle
              check_id={row.original.id}
              check_number={row.original.number}
              state={applied_state}
              on_change={(updated: CheckDetail) => on_applied_change(row.original.number, updated)}
              size="sm"
              className="h-6 px-2 text-xs"
            />
          )
          return (
            <div class="flex items-center gap-1">
              {applied_state === "manual" ? (
                <span class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-sm bg-green-100 text-green-800">
                  <Check className="w-3 h-3" /> Applied (manual)
                </span>
              ) : (
                <span class="px-2 py-1 text-xs font-medium rounded-sm bg-amber-100 text-amber-800">Not applied</span>
              )}
              {manual_toggle}
            </div>
          )
        }
        // Prefer the payment deep link; fall back to the credit memo deep link when only the credit memo matched.
        const href = paymentId
          ? applied_remote_link(accounting_integration, "payment", paymentId)
          : applied_remote_link(accounting_integration, "credit_memo", creditMemoId!)
        const badge_class = "inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-sm bg-green-100 text-green-800"
        // No deep link for this provider (Sage/NetSuite) — static badge.
        if (!href) {
          return (
            <span class={badge_class}>
              <Check className="w-3 h-3" /> Applied
            </span>
          )
        }
        return (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            onClick={(e: MouseEvent) => e.stopPropagation()}
            class={`${badge_class} hover:underline`}>
            <Check className="w-3 h-3" /> Applied
          </a>
        )
      },
    },
    {
      accessorKey: "amount",
      header: ({ column }) => <ColumnHeader className="justify-end" column={column} title="Check Amount" />,
      enableGlobalFilter: true,
      cell: ({ row }) => currency_cell(row.getValue("amount") as number | undefined),
    },
    {
      accessorKey: "computed_check_total",
      header: ({ column }) => <ColumnHeader className="justify-end" column={column} title="Computed Check Total" />,
      enableGlobalFilter: true,
      cell: ({ row }) => currency_cell(row.getValue("computed_check_total") as number | undefined),
    },
    {
      accessorKey: "invoices_total",
      header: ({ column }) => <ColumnHeader className="justify-end" column={column} title="Invoices" />,
      enableGlobalFilter: true,
      cell: ({ row }) => currency_cell(row.getValue("invoices_total") as number | undefined),
    },
    {
      accessorKey: "repayments_total",
      header: ({ column }) => <ColumnHeader className="justify-end" column={column} title="Repayments" />,
      enableGlobalFilter: true,
      cell: ({ row }) => currency_cell(row.getValue("repayments_total") as number | undefined),
    },
    {
      accessorKey: "deductions_total",
      header: ({ column }) => <ColumnHeader className="justify-end" column={column} title="Deductions" />,
      enableGlobalFilter: true,
      cell: ({ row }) => currency_cell(row.getValue("deductions_total") as number | undefined),
    },
    {
      accessorKey: "deduction_rate_pct",
      header: ({ column }) => <ColumnHeader className="justify-end" column={column} title="Deduction Rate" />,
      enableGlobalFilter: true,
      cell: ({ row }) => {
        const rate = row.getValue("deduction_rate_pct") as number | null
        return (
          <div class={`text-right font-medium ${check_deduction_rate_color_class(rate)}`}>
            {format_deduction_rate_pct(rate)}
          </div>
        )
      },
    },
    {
      id: "files",
      header: ({ column }) => <ColumnHeader column={column} title="Files" />,
      enableGlobalFilter: false,
      cell: ({ row }) => {
        const check = row.original
        const s3_uri = check.s3_uri
        if (!s3_uri) return ""
        return (
          <div onClick={handleViewS3Uri(s3_uri)} className="cursor-pointer text-black underline pr-2">
            <File className="w-4 h-4" />
          </div>
        )
      },
    }
  )

  return columns
}

/**
 * The body for `POST /accounting/checks`. The date kind always goes with the range: without it the
 * server filters on invoice date, whatever the toggle says. Super Coffee's Polar check 00621741
 * (check date 9/23, invoices dated June to August) was missing from a September range set to
 * "Check Date" because the kind was only sent for orgs with a posting-date field.
 */
export function check_list_request_body(
  start: Date | undefined | null,
  end: Date | undefined | null,
  distributor: string | undefined | null,
  date_kind: DateKind
) {
  return {
    start_date: map(start, toISODateString),
    end_date: map(end, toISODateString),
    distributor,
    date_kind,
  }
}

export function AccountingChecks() {
  usePageTitle("Checks")
  const [location, setLocation] = useLocation()
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() => restored_customer_filter(read_stored_customer(), null))
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = useState({})
  const [globalFilter, setGlobalFilter] = useState("")
  const [bulkApplyOpen, setBulkApplyOpen] = useState(false)
  // Frozen when the dialog opens: applying a check flips its row to applied, which would otherwise
  // drop it from the run's own list mid-run.
  const [bulkApplyChecks, setBulkApplyChecks] = useState<CheckDetail[]>([])
  const orgConfig = OrgConfigState.use()
  const orgSettings = useOrgSettings()

  // The global (source-based) customer filter is hidden on this page in favour of its own customer
  // select, so a source picked on another page must not silently narrow this list.
  const { startDate: start, endDate: end, dateKind } = GlobalFilterState.use()
  const showPostingDateColumn =
    posting_date_filter_enabled(orgConfig.orgFieldConfigs, "check_detail_table") ||
    posting_date_filter_enabled(orgConfig.orgFieldConfigs, "accounting_table")
  const postingDateLabel =
    orgConfig.orgFieldConfigs.find(c => c.field_name === "posting_date" && c.is_active)?.display_name ?? "Posting Date"

  useAsyncEffect(async () => {
    await loadOrgFieldConfigs()
  }, [])

  let { loading, data, setData } = useFetch<CheckDetail[]>(
    () =>
      api_fetch_throws<CheckDetail[]>(`/accounting/checks`, {
        body: check_list_request_body(start, end, null, dateKind),
      }),
    [start, end, dateKind],
    []
  )

  // Mireya (Super Coffee AR) works the list one customer at a time; Grocery Supply and most
  // regional distributors share the `other` source, so the source-based global filter cannot pick them.
  const customer_choices = useMemo(() => check_customer_choices(data), [data])
  const selected_customer = selected_check_customer(columnFilters)

  useEffect(() => {
    write_stored_customer(selected_customer)
  }, [selected_customer])

  // Drop a remembered customer the loaded checks no longer include, so the list is never empty
  // behind a filter whose select has no option for it.
  useEffect(() => {
    if (loading || !selected_customer) return
    if (restored_customer_filter(selected_customer, customer_choices).length === 0) {
      setColumnFilters((filters: ColumnFiltersState) => filters.filter((filter) => filter.id !== "customer_name"))
    }
  }, [loading, customer_choices, selected_customer])

  // The Applied cell patches its row in place; a refetch would flash the whole table to skeletons.
  const columns = useMemo(
    () =>
      build_check_list_columns(
        showPostingDateColumn,
        postingDateLabel,
        orgSettings.accounting_integration,
        customer_choices,
        (number: string, updated: CheckDetail) =>
          setData((rows: CheckDetail[]) => apply_manual_applied_update(rows, number, updated))
      ),
    [showPostingDateColumn, postingDateLabel, orgSettings.accounting_integration, customer_choices]
  )

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getColumnCanGlobalFilter: c => c.columnDef.enableGlobalFilter ?? true,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageSize: 25,
      },
    },
    meta: {
      clearAllFilters: () => {
        setColumnFilters([])
        setGlobalFilter("")
      },
    },
  })

  function handleRowClick(check: CheckDetail, event: MouseEvent) {
    if (event.metaKey || event.ctrlKey) {
      window.open(check_detail_path(check.number), "_blank")
    } else {
      setLocation(check_detail_path(check.number))
    }
  }

  const rows = table.getRowModel().rows
  const customer_column = table.getColumn("customer_name")
  const column_count = table.getVisibleLeafColumns().length

  function open_bulk_apply() {
    // Everything the filters leave in the list, not just the visible page.
    setBulkApplyChecks(bulk_apply_candidates(table.getFilteredRowModel().rows.map(row => row.original)))
    setBulkApplyOpen(true)
  }

  return (
    <div class="w-full">
      <div className="px-4">
        <CheckNavbar>
          <GlobalFilters show_customer_filter={false} />
        </CheckNavbar>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold">Cash Application</h1>
            <span className="text-gray-500 text-sm block">Record checks into your accounting system.</span>
          </div>
          <div className="flex items-center gap-2">
            {customer_column && customer_choices.length > 1 && (
              <div class="w-56">
                <ColumnChoiceFilter column={customer_column} choices={customer_choices} all_label="All customers" />
              </div>
            )}
            {orgSettings.accounting_integration ? (
              <Button variant="outline" disabled={loading} onClick={open_bulk_apply}>
                Apply ready checks
              </Button>
            ) : null}
            <Button onClick={() => setLocation("/accounting/check/new")}>New Check</Button>
          </div>
        </div>
        <BulkApplyDialog
          open={bulkApplyOpen}
          on_close={() => setBulkApplyOpen(false)}
          checks={bulkApplyChecks}
          accounting_system={get_integration_label(orgSettings.accounting_integration)}
          on_applied={(number: string, payment_remote_id: string | undefined) =>
            setData((rows: CheckDetail[]) => apply_bulk_applied_update(rows, number, payment_remote_id))
          }
        />
      </div>
      <div className="mx-auto mb-4 px-4">
        <TableControls table={table as any as Table<any>} />
        <div class="border border-gray-200 rounded-lg w-full overflow-hidden">
          <div class="overflow-auto w-full">
            <table class="w-full border-collapse" style={{ tableLayout: "auto" }}>
              <thead class="bg-slate-50">
                {table.getHeaderGroups().map((headerGroup, idx) => (
                  <tr key={`${headerGroup.id}-${idx}`} className="border-b border-gray-200">
                    {headerGroup.headers.map((header, headerIdx) => (
                      <th
                        key={`${header.id}-${headerIdx}`}
                        className={`px-1 py-1 text-xs font-medium text-slate-700 whitespace-nowrap ${
                          RIGHT_ALIGNED_COLUMN_IDS.has(header.column.id) ? "text-right" : "text-left"
                        }`}
                        style={{
                          position: "sticky",
                          top: 0,
                          backgroundColor: "#f8fafc",
                          zIndex: 10,
                          boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                          maxWidth: "180px",
                          width: "auto",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={header.column.id ? capitalCase(header.column.id) : ""}>
                        {header.isPlaceholder ? null : (
                          <div
                            className={
                              RIGHT_ALIGNED_COLUMN_IDS.has(header.column.id) ? "flex justify-end items-center" : ""
                            }>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    {Array.from({ length: column_count || columns.length }).map((_, cellIndex) => (
                      <td key={cellIndex} className="px-4 py-3 border-b">
                        <Skeleton className="w-[100px] h-5 rounded-full bg-slate-500" />
                      </td>
                    ))}
                  </tr>
                ) : rows.length ? (
                  rows.map((row, idx) => (
                    <tr
                      key={`${row.id}-${idx}`}
                      className="hover:bg-slate-50 cursor-pointer"
                      onClick={(event: MouseEvent) => handleRowClick(row.original, event)}
                      data-state={row.getIsSelected() && "selected"}>
                      {row.getVisibleCells().map((cell, cellIdx) => (
                        <td
                          className={`px-1 py-1 border-b border-gray-200 whitespace-nowrap text-xs ${
                            RIGHT_ALIGNED_COLUMN_IDS.has(cell.column.id) ? "text-right" : ""
                          }`}
                          style={{
                            maxWidth: "180px",
                            width: "auto",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={String(cell.getValue() || "")}
                          key={`${cell.id}-${cellIdx}`}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={column_count || columns.length} className="h-24 text-center">
                      No results.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {Object.keys(rowSelection).length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 mx-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium text-blue-900">
                {table.getFilteredSelectedRowModel().rows.length} check(s) selected
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  className="h-8 px-3"
                  onClick={() => {
                    console.log("Export selected checks")
                    // TODO: Implement export functionality
                  }}>
                  Export Selected
                </Button>
                <Button
                  variant="outline"
                  className="h-8 px-3"
                  onClick={() => {
                    console.log("Mark as coded")
                    // TODO: Implement mark as coded functionality
                  }}>
                  Mark as Coded
                </Button>
                <Button variant="outline" className="h-8 px-3" onClick={() => setRowSelection({})}>
                  Clear Selection
                </Button>
              </div>
            </div>
          </div>
        )}
        <div class="flex items-center justify-between p-2 px-4">
          <PageControls table={table} />
        </div>
      </div>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  describe("check_list_request_body", () => {
    it("sends the selected date kind with the range, for every kind", () => {
      const kinds: DateKind[] = ["invoice", "check", "posting", "deposit"]
      for (const kind of kinds) {
        const body = check_list_request_body(new Date(2026, 8, 1), new Date(2026, 8, 30), undefined, kind)
        expect(body.date_kind).toBe(kind)
        expect(body.start_date).toBe("2026-09-01")
        expect(body.end_date).toBe("2026-09-30")
      }
    })
  })

  describe("customer filter", () => {
    const checks = [
      { customer_name: "UNFI" },
      { customer_name: "Grocery Supply Company" },
      { customer_name: "UNFI" },
      { customer_name: "" },
      { customer_name: undefined },
    ] as Pick<CheckDetail, "customer_name">[]

    it("offers each customer on the loaded checks once, A to Z", () => {
      expect(check_customer_choices(checks)).toEqual([
        { value: "Grocery Supply Company", label: "Grocery Supply Company" },
        { value: "UNFI", label: "UNFI" },
      ])
    })

    it("keeps only the picked customer's checks", () => {
      const rows = checks.map((check) => ({ getValue: () => check.customer_name }))
      expect(rows.filter((row) => customer_equals_filter(row, "customer_name", "Grocery Supply Company"))).toHaveLength(1)
      expect(rows.filter((row) => customer_equals_filter(row, "customer_name", "UNFI"))).toHaveLength(2)
    })

    it("reads the picked customer out of the column filters", () => {
      expect(selected_check_customer([{ id: "status", value: "new" }])).toBeUndefined()
      expect(selected_check_customer([{ id: "customer_name", value: "UNFI" }])).toBe("UNFI")
      expect(selected_check_customer([{ id: "customer_name", value: "" }])).toBeUndefined()
    })

    it("restores a remembered customer only while a loaded check belongs to it", () => {
      const choices = check_customer_choices(checks)
      expect(restored_customer_filter(null, choices)).toEqual([])
      // Still loading: keep it until the checks say otherwise.
      expect(restored_customer_filter("Grocery Supply Company", null)).toEqual([{ id: "customer_name", value: "Grocery Supply Company" }])
      expect(restored_customer_filter("Grocery Supply Company", choices)).toEqual([{ id: "customer_name", value: "Grocery Supply Company" }])
      expect(restored_customer_filter("Walmart", choices)).toEqual([])
    })
  })

  describe("apply_manual_applied_update", () => {
    const row = (number: string, extra: Partial<CheckDetail> = {}) =>
      ({ number, source: "kehe", transactions: [], status: "coded", ...extra }) as CheckDetail

    it("stamps the toggled check's id and applied state", () => {
      const rows = [row("100"), row("200")]
      const updated = { id: "ck1", number: "100", applied_at: "2026-09-15T00:00:00Z", applied_source: "manual" } as CheckDetail
      const result = apply_manual_applied_update(rows, "100", updated)
      expect(result[0]).toMatchObject({ id: "ck1", applied_at: "2026-09-15T00:00:00Z", applied_source: "manual" })
      expect(result[1]).toBe(rows[1])
    })

    it("clears applied state when the server returns nulls", () => {
      const rows = [row("100", { id: "ck1", applied_at: "2026-09-15T00:00:00Z", applied_source: "manual" })]
      const updated = { id: "ck1", number: "100", applied_at: null, applied_source: null } as unknown as CheckDetail
      const result = apply_manual_applied_update(rows, "100", updated)
      expect(result[0].applied_at).toBeUndefined()
      expect(result[0].applied_source).toBeUndefined()
    })
  })

  describe("bulk_apply_candidates", () => {
    const row = (number: string, extra: Partial<CheckDetail> = {}) =>
      ({ number, source: "walmart", transactions: [], status: "coded", ...extra }) as CheckDetail

    it("keeps unapplied checks only, oldest first", () => {
      const rows = [
        row("300", { date: "2026-09-23" }),
        row("100", { date: "2026-05-26" }),
        row("200", { date: "2026-07-01", applied_payment_remote_id: "pay-1" }),
        row("250", { date: "2026-07-02", applied_at: "2026-07-03T00:00:00Z", applied_source: "manual" }),
      ]
      expect(bulk_apply_candidates(rows).map((r: CheckDetail) => r.number)).toEqual(["100", "300"])
    })

    it("shows a bulk-applied check as applied without a refetch", () => {
      const [updated, untouched] = apply_bulk_applied_update([row("100"), row("200")], "100", "pay-9")
      expect(check_applied_state(updated)).toBe("remote")
      expect(check_applied_state(untouched)).toBe("not_applied")
      // A provider that returns no payment id still reads as applied.
      expect(check_applied_state(apply_bulk_applied_update([row("100")], "100", undefined)[0])).toBe("manual")
    })
  })

  describe("format_deduction_rate_pct", () => {
    it("formats finite rates", () => {
      expect(format_deduction_rate_pct(19.05)).toBe("19.1%")
    })

    it("formats rates over 100%", () => {
      expect(format_deduction_rate_pct(150.25)).toBe("150.3%")
      expect(format_deduction_rate_pct(91.30738213399503)).toBe("91.3%")
    })

    it("shows em dash for null", () => {
      expect(format_deduction_rate_pct(null)).toBe("—")
    })
  })

  describe("check_deduction_rate_color_class", () => {
    it("is muted when rate is unavailable (deduction-only ACH checks)", () => {
      expect(check_deduction_rate_color_class(null)).toBe("text-muted-foreground")
    })

    it("is green under 20%", () => {
      expect(check_deduction_rate_color_class(0)).toBe("text-green-600")
      expect(check_deduction_rate_color_class(4.4973076076405025)).toBe("text-green-600")
      expect(check_deduction_rate_color_class(11.580493940510458)).toBe("text-green-600")
      expect(check_deduction_rate_color_class(19.9)).toBe("text-green-600")
    })

    it("is neutral between 20% and 30% inclusive", () => {
      expect(check_deduction_rate_color_class(20)).toBe("")
      expect(check_deduction_rate_color_class(23.85120350109409)).toBe("")
      expect(check_deduction_rate_color_class(28.56515842619407)).toBe("")
      expect(check_deduction_rate_color_class(30)).toBe("")
    })

    it("is red over 30% including rates above 100%", () => {
      expect(check_deduction_rate_color_class(30.1)).toBe("text-red-600")
      expect(check_deduction_rate_color_class(47.6060241509809)).toBe("text-red-600")
      expect(check_deduction_rate_color_class(76.41825618879298)).toBe("text-red-600")
      expect(check_deduction_rate_color_class(91.30738213399503)).toBe("text-red-600")
      expect(check_deduction_rate_color_class(150)).toBe("text-red-600")
    })
  })
}
